import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useFieldDecimalPlacesConfig } from 'hooks';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { getErrors, isValid, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { ADD_UPDATE_LEASING_INTEREST_TYPE_DETAIL } from '../mutations';
import { LEASING_INTEREST_TYPE_DETAIL_SAVED } from '../subscriptions';
import { CURRENCIES_DICTIONARY_QUERY, LEASING_INTEREST_TYPES_QUERY } from 'features/common/queries';
import { useApolloClient } from '@apollo/client';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useToast } from 'hooks/toasts/useToast';
import { Autocomplete } from '@totalsoft/rocket-ui';
import { Grid } from '@mui/material';
import { Button } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import { Prompt } from 'react-router-dom';
import { TextField } from '@totalsoft/rocket-ui';
import { leasingDetailModalVar } from '../../variables';
import { interestDetailValidator } from '../validators';

const DetailComponent = ({ detail }) => {
  const client = useApolloClient();
  const addToast = useToast();
  const { t } = useTranslation();

  const { cvsChPercent6 } = useFieldDecimalPlacesConfig();
  const [saving, setSaving] = useState(false);
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens({});
  const [validation, validate] = useDirtyFieldValidation(interestDetailValidator);

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  const { data: leasingInterestTypesData, refetch } = useQuery(LEASING_INTEREST_TYPES_QUERY, {
    skip: !get(lens.currencyId),
    variables: { currencyId: get(lens.currencyId) }
  });

  const { data: currenciesDictionaryData } = useQuery(CURRENCIES_DICTIONARY_QUERY);

  const currencies = useMemo(
    () => (currenciesDictionaryData && currenciesDictionaryData.currenciesDictionary) || [],
    [currenciesDictionaryData]
  );

  useEffect(() => {
    // eslint-disable-next-line no-unused-vars
    const { interestTypeName, ...rest } = detail;
    resetLens(rest);
  }, [resetLens, detail]);

  useEffect(() => {
    refetch({
      variables: { currencyId: get(lens.currencyId) }
    });
  }, [refetch, lens.currencyId]);

  useEffect(() => {
    validate(get(lens), dirtyInfo);
  }, [validate, lens, dirtyInfo]);

  // #region Save

  const [addOrUpdateLeasingInterestTypeDetail] = useMutation(ADD_UPDATE_LEASING_INTEREST_TYPE_DETAIL, {
    onError: () => {
      setSaving(false);
    }
  });

  const handleSave = useCallback(() => {
    const detail = get(lens);
    if (!validate(detail)) return;
    setSaving(true);
    // eslint-disable-next-line no-unused-vars
    const { currencyId, currencyCode, ...rest } = detail;
    addOrUpdateLeasingInterestTypeDetail({ variables: { input: { ...rest } } });
  }, [validate, lens, addOrUpdateLeasingInterestTypeDetail]);

  useSubscription(LEASING_INTEREST_TYPE_DETAIL_SAVED, {
    onData: _ => {
      addToast(t('LeasingInterestTypeDetail.Saved'), 'success');
      if (!detail.id) leasingDetailModalVar({ open: false });
      else setSaving(false);

      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedLeasingInterestTypeDetails' });
      client.cache.gc();
    }
  });
  // #endregion

  const currencyChange = useCallback(
    value => {
      set(lens.currencyId)(value.id);
      set(lens.currencyCode)(value.code);
    },
    [lens.currencyId, lens.currencyCode]
  );

  return (
    <>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Autocomplete
            id='leasingInterestTypeDetail_currency'
            label={t('LeasingInterestTypeDetail.Currency')}
            fullWidth
            required
            valueKey={'id'}
            labelKey={'code'}
            value={{ id: lens.currencyId |> get, code: lens.currencyCode |> get }}
            onChange={currencyChange}
            options={currencies}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            id='leasingInterestTypeDetail_interestTypeName'
            label={t('LeasingInterestTypeDetail.InterestTypeName')}
            fullWidth
            isSearchable
            isClearable
            required
            simpleValue
            valueKey={'interestTypeId'}
            labelKey={'interestTypeName'}
            value={get(lens.interestTypeId)}
            onChange={set(lens.interestTypeId)}
            disabled={!(lens.currencyId |> get)}
            options={(leasingInterestTypesData && leasingInterestTypesData.leasingInterestTypes) || []}
            error={!isValid(validation.interestTypeId)}
            helperText={getErrors(validation.interestTypeId)}
          />
        </Grid>
        <Grid item xs={6}>
          <DateTime
            id='leasingInterestTypeDetail_startDate'
            label={t('LeasingInterestTypeDetail.StartDate')}
            timeFormat={false}
            required
            isClearable
            value={get(lens.startDate)}
            onChange={set(lens.startDate)}
            error={!isValid(validation.startDate)}
            helperText={getErrors(validation.startDate)}
          />
        </Grid>
        <Grid item xs={6}>
          <DateTime
            id='leasingInterestTypeDetail_endDate'
            label={t('LeasingInterestTypeDetail.EndDate')}
            timeFormat={false}
            required
            isClearable
            value={get(lens.endDate)}
            onChange={set(lens.endDate)}
            error={!isValid(validation.endDate)}
            helperText={getErrors(validation.endDate)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            id='leasingInterestTypeDetail_interestPercent'
            label={t('LeasingInterestTypeDetail.InterestPercent')}
            isNumeric
            fullWidth
            required
            value={get(lens.interestPercent)}
            onChange={set(lens.interestPercent)}
            decimalScale={cvsChPercent6}
            allowEmptyFormatting={true}
            error={!isValid(validation.interestPercent)}
            helperText={getErrors(validation.interestPercent)}
          />
        </Grid>
        <Grid container item xs={12} justifyContent='flex-end'>
          <Button onClick={handleSave} disabled={saving} sx={{ mr: 1 }}>
            {saving ? t('Saving') : t('Save')}
          </Button>
          <Button onClick={() => leasingDetailModalVar({ open: false })} disabled={saving}>
            {t('Close')}
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

DetailComponent.propTypes = {
  detail: PropTypes.object
};

export default DetailComponent;
